import React, { useState } from 'react';
import { X, Send, ChevronRight, ChevronLeft, Loader2, CheckCircle, Sprout, Building2, Shield, FileText } from 'lucide-react';
import { profileRequestAPI } from '../../services/api';

// ─── Helper to get nested dotted key paths safely ───────────
function getNestedValue(obj, path) {
  if (!obj || !path) return '';
  const keys = path.split('.');
  let curr = obj;
  for (const k of keys) {
    if (curr === undefined || curr === null) return '';
    curr = curr[k];
  }
  return curr !== undefined && curr !== null ? curr : '';
}

// ─── Helper to set nested dotted key paths ───────────
function setNestedValue(obj, path, value) {
  const keys = path.split('.');
  const result = { ...obj };
  let curr = result;
  for (let i = 0; i < keys.length - 1; i++) {
    curr[keys[i]] = typeof curr[keys[i]] === 'object' ? { ...curr[keys[i]] } : {};
    curr = curr[keys[i]];
  }
  curr[keys[keys.length - 1]] = value;
  return result;
}

// ─── Farmer Form Fields ──────────────────────────────────────
function FarmerForm({ data, onChange }) {
  const field = (key, label, type = 'text', placeholder = '') => (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type}
        value={getNestedValue(data, key)}
        onChange={e => onChange(key, e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-green-400 focus:border-green-400 outline-none transition bg-white"
      />
    </div>
  );
  const sel = (key, label, options) => (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      <select
        value={getNestedValue(data, key)}
        onChange={e => onChange(key, e.target.value)}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-green-400 outline-none transition bg-white"
      >
        <option value="">-- Select --</option>
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="bg-green-50 rounded-xl p-3 border border-green-100">
        <p className="text-xs text-green-700 font-medium">🌱 Farmer Information</p>
        <p className="text-xs text-green-600 mt-0.5">Fill in your farm details. Fields marked * are required.</p>
      </div>

      {/* Personal */}
      <div>
        <h4 className="text-xs font-bold text-gray-800 mb-2 pb-1 border-b border-gray-100">Personal Details</h4>
        <div className="grid grid-cols-2 gap-3">
          {field('lastname', 'Last Name *', 'text', 'Dela Cruz')}
          {field('firstname', 'First Name *', 'text', 'Juan')}
          {field('middleInitial', 'Middle Initial', 'text', 'M')}
          {field('sitioPurok', 'Sitio/Purok', 'text', 'Sitio Mabolo')}
          {field('barangay_id', 'Barangay ID', 'number', '1')}
          {field('age', 'Age', 'number', '35')}
          {sel('gender_id', 'Gender', [{ value: '1', label: 'Male' }, { value: '2', label: 'Female' }])}
          {sel('civil_status_id', 'Civil Status', [
            { value: '1', label: 'Single' }, { value: '2', label: 'Married' },
            { value: '3', label: 'Widowed' }, { value: '4', label: 'Separated' }
          ])}
          {sel('education_id', 'Education Level', [
            { value: '1', label: 'Elementary' }, { value: '2', label: 'High School' },
            { value: '3', label: 'College' }, { value: '4', label: 'Vocational' }
          ])}
          {field('household_members', 'Household Members', 'number', '4')}
        </div>
      </div>

      {/* Income */}
      <div>
        <h4 className="text-xs font-bold text-gray-800 mb-2 pb-1 border-b border-gray-100">Income</h4>
        <div className="grid grid-cols-2 gap-3">
          {field('incomeData.annualFarmIncome', 'Annual Farm Income (₱)', 'number', '50000')}
          {field('incomeData.formOfIncome', 'Form of Income', 'text', 'Cash')}
          {field('incomeData.inPesoIncome', 'In-Peso Income (₱)', 'number')}
          {field('incomeData.inKindIncome', 'In-Kind Income', 'text')}
        </div>
      </div>

      {/* Farm */}
      <div>
        <h4 className="text-xs font-bold text-gray-800 mb-2 pb-1 border-b border-gray-100">Farm Details</h4>
        <div className="grid grid-cols-2 gap-3">
          {field('farmData.farmArea', 'Farm Area (ha)', 'number', '1.5')}
          {field('farmData.yearEstablished', 'Year Established', 'number', '2015')}
          {field('farmData.actualTrees', 'Actual Trees', 'number', '500')}
          {field('farmData.productiveTreesBefore', 'Productive Trees (Before)', 'number')}
          {field('farmData.productiveTreesAfter', 'Productive Trees (After)', 'number')}
          {field('farmData.varietyPlanted', 'Variety Planted', 'text', 'Amelonado')}
          {field('farmData.landOwnership', 'Land Ownership', 'text', 'Owner')}
          {field('farmData.propagationTechnique', 'Propagation Technique', 'text')}
        </div>
      </div>

      {/* Membership */}
      <div>
        <h4 className="text-xs font-bold text-gray-800 mb-2 pb-1 border-b border-gray-100">Cooperative / Affiliation</h4>
        <div className="grid grid-cols-2 gap-3">
          {field('membershipData.affiliationName', 'Affiliation Name', 'text', 'Bohol Cacao Cooperative')}
          {field('membershipData.yearsInMembership', 'Years in Membership', 'number')}
          {field('membershipData.position', 'Position', 'text', 'Member')}
          {sel('membershipData.active', 'Active Member?', [
            { value: '1', label: 'Yes' }, { value: '0', label: 'No' }
          ])}
        </div>
      </div>

      {/* Productivity */}
      <div>
        <h4 className="text-xs font-bold text-gray-800 mb-2 pb-1 border-b border-gray-100">Productivity</h4>
        <div className="grid grid-cols-2 gap-3">
          {field('productivityData.productivityPodsTreeYear', 'Pods/Tree/Year', 'number')}
          {field('productivityData.podsPerTreeHarvest', 'Pods/Tree/Harvest', 'number')}
          {field('productivityData.weightOfBeansPerHarvestKg', 'Beans/Harvest (kg)', 'number')}
          {field('productivityData.weightOfBeansPerHectareKg', 'Beans/Hectare (kg)', 'number')}
        </div>
      </div>
    </div>
  );
}

// ─── Processor Form Fields ───────────────────────────────────
function ProcessorForm({ data, onChange }) {
  const field = (key, label, type = 'text', placeholder = '') => (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type}
        value={getNestedValue(data, key)}
        onChange={e => onChange(key, e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-amber-400 focus:border-amber-400 outline-none transition bg-white"
      />
    </div>
  );
  const sel = (key, label, options) => (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      <select
        value={getNestedValue(data, key)}
        onChange={e => onChange(key, e.target.value)}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-amber-400 outline-none transition bg-white"
      >
        <option value="">-- Select --</option>
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="bg-amber-50 rounded-xl p-3 border border-amber-100">
        <p className="text-xs text-amber-700 font-medium">🏭 Business / Processor Information</p>
        <p className="text-xs text-amber-600 mt-0.5">Fill in your business details. Fields marked * are required.</p>
      </div>

      {/* Business Info */}
      <div>
        <h4 className="text-xs font-bold text-gray-800 mb-2 pb-1 border-b border-gray-100">Business Details</h4>
        <div className="grid grid-cols-2 gap-3">
          {field('name', 'Business Name *', 'text', 'Bohol Cacao Farm & Chocolate')}
          {field('address', 'Business Address *', 'text', 'Tagbilaran City, Bohol')}
          {field('contact', 'Contact Number', 'tel', '09XX XXX XXXX')}
          {field('owner', 'Owner/Manager Name', 'text')}
          {field('years', 'Years in Operation', 'number', '5')}
          {field('employees', 'No. of Employees', 'number')}
          {field('investment', 'Total Investment (₱)', 'number')}
          {field('income', 'Annual Income (₱)', 'number')}
          {sel('status', 'Business Status', [
            { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }
          ])}
          {sel('businessType', 'Business Type', [
            { value: 'sole', label: 'Sole Proprietorship' },
            { value: 'partnership', label: 'Partnership' },
            { value: 'cooperative', label: 'Cooperative' },
            { value: 'corporation', label: 'Corporation' }
          ])}
        </div>
      </div>

      {/* Processing */}
      <div>
        <h4 className="text-xs font-bold text-gray-800 mb-2 pb-1 border-b border-gray-100">Processing Capacity</h4>
        <div className="grid grid-cols-2 gap-3">
          {field('processingData.cacaoQty', 'Cacao Quantity (kg)', 'number')}
          {field('processingData.processingFrequency', 'Processing Frequency', 'text', 'Weekly')}
          {field('processingData.preferredVariety', 'Preferred Variety', 'text')}
          {field('processingData.processedVariety', 'Processed Variety', 'text')}
          {field('processingData.sourceCacao', 'Source of Cacao', 'text', 'Local Farmers')}
          {field('processingData.cacaoForm', 'Form of Cacao', 'text', 'Dried Beans')}
        </div>
      </div>

      {/* Marketing */}
      <div>
        <h4 className="text-xs font-bold text-gray-800 mb-2 pb-1 border-b border-gray-100">Products & Marketing</h4>
        <div className="grid grid-cols-2 gap-3">
          {field('marketingData.productType', 'Product Type', 'text', 'Tablea, Chocolate Bars')}
          {field('marketingData.marketingType', 'Marketing Type', 'text', 'Direct Sales')}
          {field('marketingData.market', 'Market', 'text', 'Local')}
          {field('marketingData.marketplace', 'Marketplace', 'text', 'Public Market')}
        </div>
      </div>

      {/* Insights */}
      <div>
        <h4 className="text-xs font-bold text-gray-800 mb-2 pb-1 border-b border-gray-100">Insights</h4>
        <div className="grid grid-cols-1 gap-3">
          {field('insightsData.interest', 'Interests / Goals', 'text', 'Expand market reach')}
          {field('insightsData.challenges', 'Challenges Faced', 'text', 'Supply instability')}
          {field('insightsData.supports', 'Needed Support', 'text', 'Training, Equipment')}
          {field('insightsData.plans', 'Future Plans', 'text', 'Export to Manila market')}
        </div>
      </div>
    </div>
  );
}

// ─── Main RequestPostingModal ─────────────────────────────────
export function RequestPostingModal({ user, onClose, onSuccess }) {
  const role = user?.role; // 'farmer' | 'processor'
  const isFarmer = role === 'farmer';

  const [step, setStep] = useState('form'); // 'form' | 'agreement' | 'success'
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isAgreedChecked, setIsAgreedChecked] = useState(false);

  const handleChange = (key, value) => {
    setFormData(prev => setNestedValue(prev, key, value));
  };

  const handleProceedToAgreement = () => {
    setError('');
    // Validation before showing agreement
    if (isFarmer) {
      if (!formData.lastname || !formData.firstname) {
        setError('Please enter your Last Name and First Name before proceeding.');
        return;
      }
    } else {
      if (!formData.name) {
        setError('Please enter your Business Name before proceeding.');
        return;
      }
    }
    setStep('agreement');
  };

  const handleFinalSubmit = async () => {
    if (!user?.id) {
      setError('You must be logged in to submit a posting request.');
      return;
    }
    if (!isAgreedChecked) {
      setError('Please check the agreement box before submitting.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      const response = await profileRequestAPI.submit({
        userId: user.id,
        role,
        formData,
        agreedAt: new Date().toISOString()
      });

      if (response.success) {
        setStep('success');
        if (onSuccess) onSuccess(response);
      } else {
        setError(response.message || 'Failed to submit. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const colorClass = isFarmer
    ? { header: 'from-green-600 to-emerald-500', btn: 'bg-green-600 hover:bg-green-700', ring: 'focus:ring-green-400' }
    : { header: 'from-amber-600 to-orange-500', btn: 'bg-amber-600 hover:bg-amber-700', ring: 'focus:ring-amber-400' };

  // ─── Step 1: Form Step ─────────────────────────────────────────
  if (step === 'form') {
    return (
      <div
        className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
        role="dialog"
        aria-modal="true"
      >
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] flex flex-col overflow-hidden">
          {/* Header */}
          <div className={`px-6 py-4 flex items-center justify-between bg-gradient-to-r ${colorClass.header}`}>
            <div className="flex items-center gap-3">
              {isFarmer ? <Sprout className="w-6 h-6 text-white" /> : <Building2 className="w-6 h-6 text-white" />}
              <div>
                <h2 className="text-base font-bold text-white">
                  {isFarmer ? 'Farmer Profile Posting Request' : 'Processor / Business Posting Request'}
                </h2>
                <p className="text-xs text-white/80">Step 1: Fill in your details below</p>
              </div>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white rounded-full p-1.5 hover:bg-white/10 transition">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form Body */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-xs text-red-700 font-medium">
                ⚠️ {error}
              </div>
            )}
            {isFarmer
              ? <FarmerForm data={formData} onChange={handleChange} />
              : <ProcessorForm data={formData} onChange={handleChange} />
            }
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50">
            <button onClick={onClose} className="text-xs text-gray-500 hover:text-gray-700 font-medium">
              Cancel
            </button>
            <button
              onClick={handleProceedToAgreement}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-white font-semibold text-xs ${colorClass.btn} shadow transition`}
            >
              Submit for Admin Review
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Step 2: Agreement Step (Triggered on Submit for Admin Review) ─
  if (step === 'agreement') {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[92vh] flex flex-col overflow-hidden animate-scale-in">
          {/* Header */}
          <div className={`px-6 py-4 flex items-center justify-between bg-gradient-to-r ${colorClass.header}`}>
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6 text-white" />
              <div>
                <h2 className="text-base font-bold text-white">Public Listing Agreement</h2>
                <p className="text-xs text-white/80">Step 2: Review terms and accept agreement</p>
              </div>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white rounded-full p-1.5 hover:bg-white/10 transition">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4 text-xs text-gray-700">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-xs text-red-700 font-medium">
                ⚠️ {error}
              </div>
            )}

            <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl p-3.5">
              <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-blue-900 leading-relaxed">
                Please review the terms below before submitting your {isFarmer ? 'farm' : 'business'} profile for public listing in the BCIIS directory.
              </p>
            </div>

            <div className="space-y-2.5">
              <h3 className="font-bold text-gray-900 text-sm">Terms of Disclosure:</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Your profile will be published in the <strong>BCIIS public directory</strong> after Admin approval.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>The information submitted is accurate, truthful, and authorized by you.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Registered industry members and stakeholders can view your contact & profile details.</span>
                </li>
              </ul>
            </div>

            {/* Required Agreement Checkbox */}
            <label className="flex items-start gap-3 p-4 bg-amber-50/80 border border-amber-200 rounded-xl cursor-pointer hover:bg-amber-100/80 transition my-2">
              <input
                type="checkbox"
                id="chk-agree-terms"
                checked={isAgreedChecked}
                onChange={(e) => setIsAgreedChecked(e.target.checked)}
                className="w-4 h-4 mt-0.5 text-amber-600 rounded focus:ring-amber-500 cursor-pointer flex-shrink-0"
              />
              <span className="text-xs text-amber-950 font-semibold leading-relaxed">
                I agree to publish my {isFarmer ? 'farm profile' : 'business info'} in the public directory and confirm that all details provided are true and accurate.
              </span>
            </label>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50 gap-3">
            <button
              onClick={() => setStep('form')}
              className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 font-medium"
            >
              <ChevronLeft className="w-4 h-4" />
              Back to Form
            </button>

            <button
              id="btn-agree-and-continue"
              onClick={handleFinalSubmit}
              disabled={!isAgreedChecked || isSubmitting}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-white font-bold text-xs ${
                isAgreedChecked && !isSubmitting
                  ? `${colorClass.btn} shadow-md`
                  : 'bg-gray-300 cursor-not-allowed text-gray-500'
              } transition-all duration-200`}
            >
              {isSubmitting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</>
              ) : (
                <><Send className="w-4 h-4" /> I Agree & Continue</>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Step 3: Success Step ──────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-8 text-center animate-scale-in">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Request Submitted!</h2>
        <p className="text-xs text-gray-600 mb-6 leading-relaxed">
          Your {isFarmer ? 'farm' : 'business'} profile posting request has been sent to the <strong>Admin for review</strong>. You will receive a notification once it is processed. Thank you!
        </p>
        <button
          onClick={onClose}
          className={`w-full py-2.5 rounded-xl text-white font-bold text-xs ${colorClass.btn} shadow transition`}
        >
          Done
        </button>
      </div>
    </div>
  );
}
